import { useQuery } from "@tanstack/react-query";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";

/**
 * Hook to fetch processed videos with server-side pagination using BigQuery API
 */
const useProcessedVideosPaginated = ({
  organizationId,
  currentPage = 1,
  pageSize = 25,
  orderBy = "created_on",
  orderDirection = "desc",
}) => {
  const API_BASE_URL = "https://app.mighty45.com/api";

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      "processedVideos",
      organizationId,
      currentPage,
      pageSize,
      orderBy,
      orderDirection,
    ],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      let token = null;
      try {
        const currentUser = firebase.auth().currentUser;
        if (currentUser) {
          token = await currentUser.getIdToken();
        }
      } catch (authError) {
        console.warn("Failed to get auth token:", authError);
      }

      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const apiPage = currentPage - 1;

      const params = new URLSearchParams({
        page: apiPage.toString(),
        pageSize: pageSize.toString(),
        sortField: orderBy,
        sortDirection: orderDirection,
        processedOnly: "true",
      });

      const fullUrl = `${API_BASE_URL}/v1/shared-video-history/${organizationId}?${params}`;

      const response = await fetch(fullUrl, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch videos: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch videos");
      }

      const videoData = result.data;
      const paginationData = result.pagination;

      const transformedVideos = videoData.map((video) => ({
        ...video,
        createdOn: video.createdOn?.value
          ? {
              toDate: () => new Date(video.createdOn.value),
            }
          : null,
        processedOn: video.processedOn?.value
          ? {
              toDate: () => new Date(video.processedOn.value),
            }
          : null,
      }));

      const transformedPagination = {
        currentPage: paginationData.page + 1,
        totalPages: paginationData.totalPages,
        totalVideos: paginationData.totalItems,
        hasNextPage: paginationData.hasNextPage,
        hasPreviousPage: paginationData.hasPrevPage,
      };

      return {
        videos: transformedVideos,
        pagination: transformedPagination,
      };
    },
    enabled: !!organizationId,
    staleTime: 30000,
    cacheTime: 300000,
  });

  return {
    videos: data?.videos || [],
    loading: isLoading,
    error: error?.message || null,
    pagination: data?.pagination || {
      currentPage: 1,
      totalPages: 0,
      totalVideos: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    },
    refetch,
  };
};

export default useProcessedVideosPaginated;
